import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContextController } from "context/AppContext";
import { useMutation } from "react-query";
import { useSnackbar } from "notistack";

import updateInterviewFeedback from "layouts/pages/events/actions/updateInterviewFeedback";

import { Stack, Tooltip, Menu, MenuItem, IconButton } from "@mui/material";
import {
  Info as InfoIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Description as DescriptionIcon,
  Email as EmailIcon,
  Cancel as CancelIcon,
  ThumbUpOffAlt as ThumbUpOffAltIcon,
  ThumbsUpDown as ThumbUpDownIcon,
} from "@mui/icons-material";

import MDTypography from "components/MDTypography";
import ApplicantActionModal from "layouts/pages/applicants/components/ApplicantActionModal";
import AddANoteModal from "layouts/pages/applicants/components/AddANoteModal";
import { calculateInterviewOverallFeedback } from "utils/helpers/events";
import GenericMoreButton from "components/GenericMoreButton";
import InterviewFeedbackButton from "components/InterviewFeedbackButton";

const thumbUpOptions = [
  {
    label: "None",
    color: "action",
    icon: <ThumbUpIcon fontSize="medium" color="action" />,
  },
  {
    label: "Yes",
    color: "success",
    icon: <ThumbUpIcon fontSize="medium" color="success" />,
  },
  {
    label: "Maybe",
    color: "warning",
    icon: <ThumbUpIcon fontSize="medium" color="warning" />,
  },
  {
    label: "No",
    color: "error",
    icon: <ThumbDownIcon fontSize="medium" color="error" />,
  },
];

const TeamMemberActions = ({
  id,
  parentComponent,
  row,
  onChangeFeedback,
  selectedApplicant,
  onOpenNotes,
  event,
  notesDisabled,
  isExternalMember,
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { currentLoggedUser } = useAppContextController();
  const [isMailModalOpen, setMailModalOpen] = useState(false);
  const [isNotesModalOpen, setNotesModalOpen] = useState(false);
  const [toastAlert, setToastAlert] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const isThumbsUpOpen = Boolean(anchorEl);

  const handleClick = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const thumbIcon = useMemo(() => {
    if (!selectedApplicant)
      return {
        label: "Select an applicant to show feedback",
        color: "action",
        icon: <ThumbUpOffAltIcon fontSize="medium" color="action" />,
      };
    const feedbackItem = selectedApplicant?.feedback?.find((fb) => fb.createAgent === id);
    if (!feedbackItem) {
      return thumbUpOptions[0];
    }
    const result = thumbUpOptions.find((option) => {
      return option.label === feedbackItem.rating;
    });
    if (result) return result;
    return thumbUpOptions[0];
  }, [id, selectedApplicant]);

  const tableActions = [
    ...(!isExternalMember
      ? [
          {
            label: `User Info`,
            icon: (
              <IconButton
                sx={{ p: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    `/${parentComponent.toLowerCase()}s/${id}/action/${parentComponent.toLowerCase()}info`
                  );
                }}
              >
                <InfoIcon fontSize="medium" color="info" />
              </IconButton>
            ),
          },
        ]
      : []),
    ...(selectedApplicant
      ? [
          {
            label: `Feedback`,
            noTooltip: true,
            icon: (
              <InterviewFeedbackButton
                sx={{ padding: 0 }}
                applicant={selectedApplicant}
                // refetchApplicants={refresh}
                user={row}
                onChangeFeedback={onChangeFeedback}
                eventUrl={event?.eventUrl}
                disableFinal
              />
            ),
          },
          {
            label: notesDisabled ? "No Notes" : "Notes",
            icon: (
              <span>
                <IconButton
                  sx={{ p: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenNotes(row);
                  }}
                  disabled={notesDisabled}
                >
                  <DescriptionIcon fontSize="medium" color={notesDisabled ? "secondary" : "info"} />
                </IconButton>
              </span>
            ),
          },
        ]
      : []),
  ];

  return (
    <Stack display="flex" direction="row" justifyContent="space-between" spacing={2}>
      {tableActions.map((action) => (
        <>
          {action.noTooltip ? (
            action.icon
          ) : (
            <Tooltip title={action.label} placement="top">
              {action.icon}
            </Tooltip>
          )}
        </>
      ))}
    </Stack>
  );
};

export default TeamMemberActions;
